import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { httpsCallable } from 'firebase/functions';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { functions } from '../firebase';
import Footer from '../components/Footer';

const ContactForm = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  backgroundColor: '#FFFFFF',
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const ContactInfoBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  backgroundColor: '#FFFFFF',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const ContactIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  color: theme.palette.primary.main,
}));

const contactInfo = {
  address: '590 Lakeshore Blvd.\nIncline Village, NV 89451',
  phone: '(775) 240-0241',
  fax: '(775) 832-3331',
  email: 'vince@scottdevelopmentcompanies.com',
};

export default function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    try {
      const sendEmail = httpsCallable(functions, 'sendContactEmail');
      await sendEmail(formData);
      
      setSnackbar({
        open: true,
        message: 'Message sent successfully!',
        severity: 'success',
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to send message. Please try again. ${error}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ width: '100%', height: '100%', minWidth: '100vw', minHeight: '100vh', bgcolor: 'background.default', mt: { xs: 0, md: '3.5rem' } }}>
        <Container sx={{ py: 8 }}>
          <Typography variant="h2" component="h1" gutterBottom align="center" color="primary" fontWeight={700}>
            Contact Us
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph align="center" sx={{ mb: 6 }}>
            Get in touch with our sustainable development team
          </Typography>

          <Grid container spacing={4} sx={{ display: 'flex',  alignItems: 'center' }}>
            <Grid item xs={12} md={6}>
              <ContactForm elevation={3}>
                <Typography variant="h4" gutterBottom color="primary">
                  Send us a message
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        autoComplete="given-name"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        autoComplete="family-name"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Email Address"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        autoComplete="tel"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        multiline
                        rows={4}
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        disabled={loading}
                        sx={{
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        }}
                      >
                        {loading ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </ContactForm>
            </Grid>

            <Grid item xs={12} md={6}>
              <ContactInfoBox>
                <Typography variant="h4" gutterBottom color="primary">
                  Contact Information
                </Typography>
                <ContactIcon>
                  <LocationOnIcon sx={{ mr: 2, fontSize: 28 }} />
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {contactInfo.address}
                  </Typography>
                </ContactIcon>
                <ContactIcon>
                  <PhoneIcon sx={{ mr: 2, fontSize: 28 }} />
                  <Typography variant="body1" onClick={() => window.open(`tel:${contactInfo.phone}`, '_blank')} sx={{ cursor: 'pointer' }}>
                    {contactInfo.phone}
                  </Typography>
                </ContactIcon>
                {/* <ContactIcon>
                  <PhoneIcon sx={{ mr: 2, fontSize: 28 }} />
                  <Typography variant="body1">
                    {contactInfo.fax}
                  </Typography>
                </ContactIcon> */}
                <ContactIcon>
                  <EmailIcon sx={{ mr: 2, fontSize: 28 }} />
                  <Typography variant="body1" onClick={() => window.open(`mailto:${contactInfo.email}`, '_blank')} sx={{ cursor: 'pointer' }}>
                    {contactInfo.email}
                  </Typography>
                </ContactIcon>
              </ContactInfoBox>
            </Grid>
          </Grid>
        </Container>
        <Footer />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
  );
} 